import { useQuery } from '@tanstack/react-query';

import { t } from '../../lib/i18n';
import { toast } from '../../lib/toast';
import { fetchUserDocuments } from '../clients/api';
import { getDocumentViewUrl } from '../documents/api';

interface KybDocumentsProps {
	userId: string;
}

/**
 * The applicant's uploaded KYB documents, rendered in the artboard's key/value
 * row idiom — label on the left in `cf-ink-muted`, the actionable value on the
 * right in mono, hairline between.
 *
 * Reuses the admin-documents viewer: the list comes from the clients API and
 * each file opens through the same presigned-URL endpoint the Documents screen
 * uses, so there is exactly one way a scan reaches an operator's eyes.
 */
export function KybDocuments({ userId }: KybDocumentsProps) {
	const documentsQuery = useQuery({
		queryKey: ['dealer-kyb', userId],
		queryFn: () => fetchUserDocuments(userId),
	});

	const handleView = async (documentId: string) => {
		try {
			const data = await getDocumentViewUrl(documentId);
			window.open(data.presignedUrl, '_blank');
		} catch {
			toast.error(t('dealer.review.docsError'));
		}
	};

	if (documentsQuery.isPending) {
		return <p className="text-xs text-cf-ink-subtle">{t('dealer.review.docsLoading')}</p>;
	}

	const documents = documentsQuery.data ?? [];
	if (documents.length === 0) {
		return <p className="text-xs text-cf-ink-subtle">{t('dealer.review.docsEmpty')}</p>;
	}

	return (
		<div className="flex flex-col">
			{documents.map((doc) => (
				<div
					key={doc._id}
					className="flex justify-between gap-2 border-b border-cf-line-soft py-1.5 text-[13px] lg:py-[7px]"
				>
					<span className="min-w-0 truncate text-cf-ink-muted">{doc.documentType}</span>
					{doc._id ? (
						<button
							type="button"
							onClick={() => handleView(doc._id!)}
							className="shrink-0 font-mono text-[11px] font-medium text-cf-brand hover:underline"
						>
							{t('dealer.review.viewDoc')}
						</button>
					) : (
						<span className="shrink-0 font-mono text-[11px] text-cf-ink-subtle">—</span>
					)}
				</div>
			))}
		</div>
	);
}
